import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ArrowRight } from "lucide-react";
import { CalendlyModal, CALENDLY_URLS } from "@/components/CalendlyModal";

const COLORS = {
  warmStone: "#faf7f2",
  oliveSage: "#a07830",
  inkGrey: "#1c1510",
  inkNavy: "#1c1510",
  goldSoft: "#c9a96e",
};

// Paths where the exit-intent should pitch the Sage $47 diagnostic instead
// of the Clarity Call. These are intellectual / mid-funnel pages where a
// $47 tripwire converts better than a 30-min call ask.
const SAGE_VARIANT_PREFIXES = [
  "/the-framework",
  "/evidence",
  "/blog",
  "/results",
];

// Don't fire on Sage's own pages, too redundant.
const SUPPRESS_PREFIXES = ["/sage"];

const matchesPrefix = (pathname, prefixes) =>
  prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export default function ExitIntentPopup() {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);

  const isSageVariant = matchesPrefix(location.pathname, SAGE_VARIANT_PREFIXES);
  const isSuppressed = matchesPrefix(location.pathname, SUPPRESS_PREFIXES);

  useEffect(() => {
    if (isSuppressed) return;

    const alreadyShown = sessionStorage.getItem("exit-intent-shown");
    if (alreadyShown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem("exit-intent-shown", "true");
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [hasShown, isSuppressed]);

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible || isSuppressed) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={`relative max-w-md w-full p-8 md:p-10 shadow-2xl animate-scale-bounce ${
          isSageVariant ? "" : "bg-white"
        }`}
        style={isSageVariant ? { backgroundColor: COLORS.inkNavy, color: COLORS.warmStone, borderLeft: `4px solid ${COLORS.oliveSage}` } : {}}
        onClick={(e) => e.stopPropagation()}
        data-testid={isSageVariant ? "exit-intent-popup-sage" : "exit-intent-popup"}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 transition-colors"
          style={{ color: isSageVariant ? "rgba(250,247,242,0.6)" : `${COLORS.inkGrey}99` }}
          data-testid="exit-popup-close"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        <div className="text-center">
          {isSageVariant ? (
            <>
              <span
                className="font-mono text-[10px] tracking-[0.3em] uppercase mb-5 block"
                style={{ color: COLORS.goldSoft }}
              >
                Before You Go
              </span>

              <h2
                className="font-serif text-2xl md:text-[28px] mb-4 leading-[1.2] font-light"
                style={{ color: COLORS.warmStone }}
              >
                Reading is awareness.<br />
                <em className="italic" style={{ color: COLORS.goldSoft }}>
                  Sage shows you why it hasn't shifted.
                </em>
              </h2>

              <p
                className="mb-7 leading-relaxed text-[15px]"
                style={{ color: "rgba(250,247,242,0.75)" }}
              >
                Bring one situation. In 10 minutes, name your specific pressure pattern. $47, instant access.
              </p>

              <Link
                to="/sage"
                onClick={handleClose}
                data-testid="exit-popup-sage-cta"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-[12px] tracking-[0.18em] uppercase font-mono hover:opacity-90 transition-opacity"
                style={{ backgroundColor: COLORS.oliveSage, color: COLORS.inkNavy }}
              >
                Try Sage. $47
                <ArrowRight size={14} />
              </Link>

              <button
                onClick={handleClose}
                className="block mx-auto mt-5 text-sm underline-grow"
                style={{ color: "rgba(250,247,242,0.6)" }}
              >
                No thanks, I&apos;ll continue browsing
              </button>
            </>
          ) : (
            <>
              <span
                className="text-[13px] tracking-wide mb-4 block"
                style={{ color: COLORS.oliveSage }}
              >
                Before You Go
              </span>

              <h2
                className="font-serif text-2xl md:text-3xl mb-4 leading-tight"
                style={{ color: COLORS.inkGrey }}
              >
                Have Questions About This Work?
              </h2>

              <p
                className="mb-6 leading-relaxed"
                style={{ color: `${COLORS.inkGrey}99` }}
              >
                A Clarity Call is a simple way to explore whether this approach resonates with you. No obligation, no pressure, just a conversation.
              </p>

              <Button
                onClick={() => setIsCalendlyOpen(true)}
                data-testid="exit-popup-cta"
                className="rounded-full px-8 py-4 text-[14px] tracking-wide font-medium transition-all duration-300 flex items-center gap-2 mx-auto btn-pulse"
                style={{
                  backgroundColor: COLORS.inkNavy,
                  color: "#faf7f2",
                }}
              >
                Book a Free Clarity Call
                <ArrowRight size={16} />
              </Button>

              <CalendlyModal
                isOpen={isCalendlyOpen}
                onClose={() => setIsCalendlyOpen(false)}
                calendlyUrl={CALENDLY_URLS.clarityCall}
                title="Book a Clarity Call"
              />

              <button
                onClick={handleClose}
                className="mt-4 text-sm underline-grow"
                style={{ color: `${COLORS.inkGrey}80` }}
              >
                No thanks, I&apos;ll continue browsing
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
